export type ISection = {
  title: string;
  forms: IForm[];
};

export type TFormType =
  | 'CHECKBOX'
  | 'COMPLEX'
  | 'DATE'
  | 'MULTIPLE_CHOICE'
  | 'SELECT'
  | 'TEXTAREA'
  | 'TIME'
  | 'TEXT';

export type IForm<TChoices extends string[] = string[]> = {
  type: TFormType;
  title: string;
  id: string;
  noteOption?: {
    type?: TFormType;
    label: string;
    id: string;
  };
  isRequired?: boolean;
  hasOtherOption?: boolean;
  delimeter?: string;
  otherOptionLabel?: string;
  choices?: string[];
  requiredIf?: {
    key: IForm<TChoices>['id'];
    value: TChoices[number];
  };
  notRequiredIf?: {
    key: IForm<TChoices>['id'];
    value: TChoices[number];
  };
  subForms?: IForm[];
  placeholder?: string;
};

export const formSections: ISection[] = [
  {
    title: 'Initial Overview CKD',
    forms: [
      {
        type: 'MULTIPLE_CHOICE',
        title: 'How long have you been treated for CKD?',
        id: 'howLongTreatedCkd',
        choices: [
          '< 6 months',
          '1 year',
          '1-2 years',
          '3-5 years',
          '> 5 years',
          'Lifetime',
        ],
        noteOption: {
          type: 'TEXTAREA',
          label: 'How does CKD affect your life?',
          id: 'howLongTreatedCkdNote',
        },
      },
      {
        type: 'MULTIPLE_CHOICE',
        title:
          "How is Patient's understanding of the effects of CKD on the body?",
        id: 'effectsOnBody',
        choices: ['Good', 'Fair', 'Poor'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Which Provider do you work with to manage CKD? (Limit 4)',
          id: 'effectsOnBodyNote',
        },
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Have you been hospitalized for CKD?',
        id: 'hospitalized',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Have you received outpatient services for CKD?',
        id: 'outpatientServices',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you have a family history of CKD?',
        id: 'familyHistory',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Have you had a pretransplant assessment?',
        id: 'pretransplantAssessment',
        hasOtherOption: false,
        choices: ['Yes', 'No'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'pretransplantAssessmentNote',
        },
      },
    ],
  },
  {
    title: 'Signs & Symptoms',
    forms: [
      {
        type: 'CHECKBOX',
        title: 'Patient recognizes these Signs and Symptoms of CKD (Limit 4)',
        id: 'recognizesSignsSymptoms',
        choices: [
          'Swelling in hands, feet, or legs',
          'Discomfort or swelling to the abdomen',
          'Frequent urinary urgency',
          'Fatigue, lack of energy',
          'Loss of appetite',
          'Nausea, vomiting, taste changes',
          'Oral ulcerations',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient on these Signs and Symptoms of CKD (Limit 4)',
        id: 'snInstructions',
        choices: [
          'Swelling in hands, feet, or legs',
          'Discomfort or swelling to the abdomen',
          'Frequent urinary urgency',
          'Fatigue, lack of energy',
          'Loss of appetite',
          'Nausea, vomiting, taste changes',
          'Oral ulcerations',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient to notify Provider if any of these Signs and Symptoms occur',
        id: 'notifyProvider',
        choices: ['Yes'],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'symptomsTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'symptomsVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'symptomsVerbalizeEducationNote',
        },
      },
    ],
  },
  {
    title: 'Limiting Fluids and Monitors Weight',
    forms: [
      {
        type: 'CHECKBOX',
        title:
          'SN educated Patient on the importance of limiting fluids (Limit 4)',
        id: 'limitingFluids',
        choices: [
          'You need water to live, but when you have kidney disease, you may not need as much. This is because damaged kidneys do not get rid of extra fluid as well as they should',
          'Too much fluid in your body can be dangerous',
          'It can cause high blood pressure, swelling and heart failure',
          'Extra fluid can also build up around your lungs and make it hard to breathe',
          'Ask your Provider or dietitian if you need to limit fluids',
          "if you do need to limit fluids, measure your fluids and drink from small cups to help you keep track of how much you've had to drink",
          'Limit sodium to help cut down on thirst. At times, you may still feel thirsty',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'To help quench your thirst, you might try to (Limit 4)',
        id: 'helpQuenchThirst',
        choices: [
          'Avoid salty foods',
          'Stay cool on hot days',
          'Chew gum',
          'Rinse your mouth',
          'Suck on a piece of ice, mints or hard candy',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'fluidsTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'fluidsVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'fluidsVerbalizeEducationNote',
        },
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you monitor weight?',
        id: 'monitorWeight',
        choices: ['No', 'Yes, every day', 'Yes, but not every day'],
      },
      {
        type: 'CHECKBOX',
        title: 'SN instructed Patient on why monitoring weight is important',
        id: 'monitorWeightImportant',
        choices: [
          'A sudden weight gain can mean more fluid is building up in your body and your heart failure is getting worse',
          'Watch for weight gain of 2-3 pounds over 1-2 days or 5 pounds over a week',
          'You can gain up to 10 Ibs of "extra" weight before feeling bad or swelling',
          'Call your provider if you have a sudden weight gain',
        ],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you have a scale?',
        id: 'haveScale',
        choices: [
          'Yes, Patient has a scale and uses it',
          'Yes, Patient has a scale but is not using it',
          'No',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN instructed Patient on consistent steps to measure weight',
        id: 'measureWeight',
        choices: [
          'Weigh at the same time in the morning, after you urinate',
          'Weigh while wearing what you sleep in, before eating or drinking',
          'Weigh using the same scale on a flat, hard surface',
          'After weighing, write your weight down on the weight log',
        ],
      },
      {
        type: 'COMPLEX',
        title: "What are Patient's recent WEIGHTS?",
        id: 'recentWeights',
        subForms: [
          {
            id: 'vitalType',
            title: 'Select Vital Type',
            type: 'SELECT',
            choices: ['Weight'],
          },
          {
            id: 'vitalDate',
            title: 'Date',
            type: 'DATE',
          },
          {
            id: 'vitalTime',
            title: 'Time',
            type: 'TIME',
          },
          {
            id: 'vitalValue',
            title: 'Pounds',
            type: 'TEXT',
            placeholder: 'ex. 150',
          },
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'weightTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'weightVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'weightVerbalizeEducationNote',
        },
      },
      {
        type: 'TEXTAREA',
        title: 'Explain need for reinforcement',
        id: 'reinforcementNote',
      },
    ],
  },
  {
    title: 'Dialysis Assessment',
    forms: [
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Do you attend dialysis?',
        id: 'dialysis',
        choices: [
          'Yes',
          'No',
          'Hemodialysis in dialysis center',
          'Hemodialysis in home',
          'Daily nocturnal hemodialysis',
          'Peritoneal dialysis with machine',
          'Peritoneal dialysis without machine',
        ],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'What type of dialysis?',
        id: 'dialysisType',
        choices: ['Peritoneal Dialysis', 'Hemodialysis'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'How often do you do dialysis?',
        id: 'dialysisFrequency',
        choices: ['3-5 hours. 3 times a week', '1½ to 2 hour, daily', 'Other'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Has a home dialysis nurse visited recently?',
        id: 'dialysisNurse',
        choices: [
          'Within the last month',
          '> 1 month ago',
          '> 6 months ago',
          'Never',
        ],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title:
          'Have you had trouble completing a full dialysis session more than 1x week?',
        id: 'dialysisSession',
        choices: ['Yes', 'No'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'How do you get access for dialysis?',
        id: 'dialysisAccess',
        choices: ['Shunt', 'Fistula', 'Graft'],
      },
      {
        type: 'CHECKBOX',
        title: 'Patient is trained and able to perform (Limit 4)',
        id: 'dialysisTrained',
        choices: [
          'All activities',
          'Insertion of needle for performing hemodialysis',
          'All necessary procedures during dialysis',
          'Maintenance of Dialysis equipment',
          'Care of vascular or peritoneal access site',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'Patient is NOT trained or able to perform (Limit 4)',
        id: 'dialysisNotTrained',
        choices: [
          'All activities',
          'Insertion of needle for performing hemodialysis',
          'All necessary procedures during dialysis',
          'Maintenance of Dialysis equipment',
          'Care of vascular or peritoneal access site',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'dialysisTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'dialysisVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'dialysisVerbalizeEducationNote',
        },
      },
    ],
  },
  {
    title: 'Caring for Fistulas and Catheters - Dialysis',
    forms: [
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient on protecting and caring for a hemodialysis fistula or graft (Limit 4)',
        id: 'hemodialysisFistula',
        choices: [
          'Do not let anyone measure your blood pressure on your access arm. Hour other arm should be used instead',
          'Do not let anyone take blood from your access arm',
          'If the vibration (thrill) or sound (bruit) of your access is absent or seems different, call your dialysis care team at once. This could mean the access is not working well',
          'Ask your dialysis care team how to make your needle sticks as comfortable as possible',
          'They can use different sites for the needles during each dialysis',
          'Or, if you have a fistula, a "button hole" technique may be used. This uses the same site for each dialysis treatment',
          'Ask your dialysis care team to teach you about preventing infection and keeping your fistula or graft working well',
          'Wash your access site every day with an antibacterial soap. Ask your dialysis cane team to recommend a good soap to use',
          'Wash access site before every dialysis treatment. Your dialysis center has hand washing sinks and antibacterial soap',
          'Do not scratch your access. Your fingernails could be a source of infection',
          'Your nurse or technician should clean your access site with an antiseptic before putting the needles in. Do not touch your access area after it has been disinfected',
          'Your nurse or technician should wear a surgical mask, a face shield and clean gloves when working near your access',
          'Avoid coughing or sneezing on your access site during treatment',
          'Always wear a clean glove or use a clean gauze pad if you are holding your own access site after the needles are pulled',
          'Apply only gentle pressure to stop bleeding after the needles are removed. Press only where the needle was and just below. Never press above where the needle was',
          'Call your dialysis care team at once if the area of the access is sore, swollen, red or feels hot. This could be a sign of infection',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient on protecting and caring for a hemodialysis catheter (Limit 4)',
        id: 'hemodialysisCatheter',
        choices: [
          'Ask your dialysis care team to teach you how to prevent infections and keep HD your catheter working well',
          'Be sure your catheter has a clean, dry dressing during and after every dialysis',
          'Make sure your nurse or technician checks your catheter for signs of infection at every visit',
          'Make sure you wear a surgical mask when you are being connected to or disconnected from the dialysis machine',
          'Make sure your nurse or technician wears a surgical mask, face shield and clean gloves when working near your catheter',
          'Ask your nurse or technician to teach you or your family how to change the dressing. Keep extra dressing supplies at home in case you need to replace your dressing',
          'Keep extra dressing supplies at home in case you need to replace your dressing',
          'Call your dialysis care team right away if your catheter cuff is showing',
        ],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient on protecting and caring for a hemodialysis fistula or graft (Limit 4)',
        id: 'hemodialysisFistulaCheck',
        choices: ['Yes'],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient on protecting and caring for a hemodialysis catheter (Limit 4)',
        id: 'hemodialysisCatheterCheck',
        choices: ['Yes'],
      },
      {
        type: 'CHECKBOX',
        title:
          'SN instructed Patient on protecting and caring for a peritoneal catheter (Limit 4)',
        id: 'peritonealCatheter',
        choices: [
          'Check your PD catheter every day for signs of cracking or pulling',
          'Do not wear tight clothes and belts around the exit site',
          'Wash your hands with soap and water for at least two minutes and dry them with a disposable paper towel before handling your PD catheter, and before and after an exchange is made',
          'Tape the PD catheter down to your skin',
          'Keep the PD catheter away from scissors or other sharp objects',
          'Cleanse the PD catheter thoroughly with a wash cloth and soap every day',
          'Keep a special dressing over the exit site if your dialysis care team tells you to do so',
          'Do not allow tugging or pulling of your PD catheter',
        ],
      },
      {
        type: 'CHECKBOX',
        title: 'SN utilized teach-back method with Patient',
        id: 'fistulaCatheterTeachBack',
        choices: ['Yes'],
      },
      {
        type: 'MULTIPLE_CHOICE',
        title: 'Was Patient able to verbalize understanding of the education?',
        id: 'fistulaCatheterVerbalizeEducation',
        choices: ['Able', 'Partially able', 'Unable'],
        noteOption: {
          type: 'TEXTAREA',
          label: 'Nurse Note:',
          id: 'fistulaCatheterVerbalizeEducationNote',
        },
      },
    ],
  },
];
